import { CompanyQuote } from 'constants/stockApi';

/**
 * Index levels that FMP no longer serves on our plan.
 *
 * Since October 29, 2026, FMP refuses S&P 500 and Dow Jones Industrial Average data:
 * `^SPX`, `^GSPC`, `^DJI` and `^DJX` return HTTP 402 on every quote, profile and
 * history endpoint. These roots read an instrument that FMP still serves, without
 * first asking for the refused symbol:
 * - SPX = `^XSP` × 10. Cboe defines XSP as exactly one tenth of SPX. FMP quotes XSP
 *   in cents, so XSP × 10 always ends in 0; `spxFromXsp` fills that digit.
 * - DJX = `DIA`. DJX is the Dow / 100. The DIA ETF closed within 0.27 DJX points of
 *   it at the median and 1.73 at most from October 2025 to October 2026.
 *
 * Databento's index spot reads the same sources (tradedata `databento/src/tickers.rs`).
 */
export interface IndexProxy {
  source: string;
  multiplier: number;
  symbol: string;
  name: string;
  // Fill the last digit that scaling leaves at 0 (SPX = XSP × 10).
  fillsLastDigit?: boolean;
}

export const INDEX_PROXIES: Record<string, IndexProxy> = {
  SPX: {
    source: '^XSP',
    multiplier: 10,
    symbol: '^SPX',
    name: 'S&P 500 Index',
    fillsLastDigit: true,
  },
  DJX: {
    source: 'DIA',
    multiplier: 1,
    symbol: '^DJX',
    name: 'Dow Jones Industrial Average / 100',
  },
};

// FMP history symbols that read a proxy. The S&P 500 comparisons request `^GSPC`.
export const HISTORY_PROXIES: Record<string, IndexProxy> = { '^GSPC': INDEX_PROXIES.SPX };

// Scale a proxy value to the index, in cents, so 772.04 × 10 reads 7720.4.
export const scaleToIndex = (value: number, proxy: IndexProxy) =>
  Math.round(value * proxy.multiplier * 100) / 100;

// Hundredths added to XSP × 10: never 0, and within -0.05..+0.04.
const SPX_HUNDREDTHS_OFFSETS = [-5, -4, -3, -2, -1, 1, 2, 3, 4];

/**
 * SPX from an XSP price in cents. XSP × 10 always ends in 0, so this fills the last
 * digit (Trey's decision, October 29, 2026) with an offset chosen by hashing the XSP
 * cent price. FMP rounds XSP to cents, so the real SPX is within 0.05 of XSP × 10; the
 * offset keeps the result in that range, never ends in 0, and keeps order (a higher
 * XSP always gives a higher SPX). The same XSP price always gives the same SPX, here
 * and in Databento (tradedata `databento/src/tickers.rs`, `spx_from_xsp`).
 */
export const spxFromXsp = (xsp: number) => {
  if (!Number.isFinite(xsp) || xsp <= 0) {
    return xsp * 10;
  }
  const cents = Math.round(xsp * 100);
  const hash = Math.imul(cents, 2654435761) >>> 0;
  const offset = SPX_HUNDREDTHS_OFFSETS[(hash >>> 16) % 9];
  return (cents * 10 + offset) / 100;
};

// An index level from a proxy price.
export const toIndexLevel = (value: number, proxy: IndexProxy) =>
  proxy.fillsLastDigit ? spxFromXsp(value) : scaleToIndex(value, proxy);

const LEVEL_FIELDS = [
  'price',
  'open',
  'previousClose',
  'dayLow',
  'dayHigh',
  'yearLow',
  'yearHigh',
] as const;
const AVERAGE_FIELDS = ['priceAvg50', 'priceAvg200'] as const;

/**
 * Present a proxy quote as its index. Levels scale and the change is recomputed from
 * them, so price − previous close still equals the change; the percentage change does
 * not scale. An index has no market cap, volume, earnings or EPS, so a fund's values
 * are cleared.
 */
export const toIndexQuote = (quote: CompanyQuote, proxy: IndexProxy): CompanyQuote => {
  const indexQuote: CompanyQuote = {
    ...quote,
    symbol: proxy.symbol,
    name: proxy.name,
    exchange: 'INDEX',
    marketCap: 0,
    volume: 0,
    avgVolume: 0,
    eps: 0,
    pe: 0,
    sharesOutstanding: 0,
    earningsAnnouncement: '',
  };
  for (const field of LEVEL_FIELDS) {
    if (typeof quote[field] === 'number') {
      indexQuote[field] = toIndexLevel(quote[field], proxy);
    }
  }
  for (const field of AVERAGE_FIELDS) {
    if (typeof quote[field] === 'number') {
      indexQuote[field] = scaleToIndex(quote[field], proxy);
    }
  }
  if (typeof quote.price === 'number' && typeof quote.previousClose === 'number') {
    indexQuote.change = Math.round((indexQuote.price - indexQuote.previousClose) * 100) / 100;
  } else if (typeof quote.change === 'number') {
    indexQuote.change = scaleToIndex(quote.change, proxy);
  }
  return indexQuote;
};
